import { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

/**
 * Switch/Toggle - con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Track: ring para definición sutil
 * - Dark mode: bg-white/5 semi-transparente
 * - Thumb: shadow con ring sutil
 * - Transiciones suaves
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ checked, onCheckedChange, size = 'md', label, className = '', id, ...props }, ref) => {
    const switchId = id || `switch-${Math.random().toString(36).substring(2, 9)}`;
    
    const trackSizes = {
      sm: 'w-8 p-0.5',
      md: 'w-11 p-0.5',
      lg: 'w-14 p-0.5',
    };
    
    const thumbSizes = {
      sm: 'size-3.5',
      md: 'size-5',
      lg: 'size-6',
    };

    const translateSizes = {
      sm: 'translate-x-3.5',
      md: 'translate-x-5',
      lg: 'translate-x-6',
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onCheckedChange) {
        onCheckedChange(e.target.checked);
      }
      if (props.onChange) {
        props.onChange(e);
      }
    };

    return (
      <label
        htmlFor={switchId}
        className={cn(
          'relative inline-flex items-center gap-3 cursor-pointer',
          props.disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          id={switchId}
          checked={checked}
          onChange={handleChange}
          className="sr-only peer"
          {...props}
        />
        {/* Track - usando ring-inset para borde interno */}
        <div
          className={cn(
            'relative inline-flex shrink-0 rounded-full',
            'transition-colors duration-200 ease-in-out',
            // Light mode - con ring inset para borde sutil
            'bg-secondary-200 ring-1 ring-inset ring-secondary-900/5',
            // Dark mode
            'dark:bg-white/5 dark:ring-white/10',
            // Checked
            'peer-checked:bg-primary-600 peer-checked:ring-primary-600',
            'dark:peer-checked:bg-primary-500 dark:peer-checked:ring-primary-500',
            // Focus
            'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-primary-600',
            'dark:peer-focus-visible:ring-primary-500 dark:peer-focus-visible:ring-offset-secondary-900',
            trackSizes[size]
          )}
        >
          {/* Thumb */}
          <span
            className={cn(
              'rounded-full bg-white shadow-sm',
              'ring-1 ring-secondary-900/5',
              'transition-transform duration-200 ease-in-out',
              // Checked: translate
              checked ? translateSizes[size] : 'translate-x-0',
              thumbSizes[size]
            )}
          />
        </div>
        {label && (
          <span className="text-sm font-medium text-secondary-900 dark:text-white">
            {label}
          </span>
        )}
      </label>
    );
  }
);

Switch.displayName = 'Switch';
